import React, { Fragment, useState } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Dialog, Transition } from '@headlessui/react';
import { NavLink } from 'react-router-dom';
import {
  XMarkIcon,
  HomeIcon,
  CubeIcon,
  ShoppingCartIcon,
  CurrencyRupeeIcon,
  UsersIcon,
  PresentationChartBarIcon,
  CogIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  HashtagIcon,
  ChartPieIcon,
  BellIcon,
  CircleStackIcon,
  PresentationChartLineIcon,
  UserGroupIcon,
  UserIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  ReceiptPercentIcon,
  TagIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  BuildingStorefrontIcon,
  ArrowsRightLeftIcon,
  InboxArrowDownIcon,
  QueueListIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArchiveBoxXMarkIcon,
  WrenchScrewdriverIcon,
  WrenchIcon,
  Cog6ToothIcon,
  LockClosedIcon,
  QrCodeIcon,
  ViewfinderCircleIcon,
  PlayCircleIcon,
  IdentificationIcon,
  PrinterIcon,
  PencilSquareIcon,
  FolderOpenIcon,
  ClipboardDocumentCheckIcon,
  ScaleIcon,
  CheckBadgeIcon,
  ChatBubbleLeftRightIcon,
  CalculatorIcon,
  DocumentCheckIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  children?: NavigationItem[];
  current?: boolean;
}

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  {
    name: 'Operations',
    icon: CubeIcon,
    children: [
      { name: 'Inventory', href: '/inventory', icon: CubeIcon },
      { name: 'Warehouses', href: '/warehouses', icon: BuildingStorefrontIcon },
      { name: 'Stock Transfers', href: '/stock-transfers', icon: ArrowsRightLeftIcon },
      { name: 'Reservations', href: '/reservations', icon: LockClosedIcon },
      { name: 'Batches & Heat Nos.', href: '/batches', icon: QrCodeIcon },
      { name: 'Serial Numbers', href: '/serials', icon: HashtagIcon },
      { name: 'Valuation & Ageing', href: '/stock-valuation', icon: BanknotesIcon },
      { name: 'Scan', href: '/scan', icon: ViewfinderCircleIcon },
      { name: 'Print Labels', href: '/labels', icon: PrinterIcon },
      { name: 'Manufacturing', href: '/manufacturing', icon: BuildingOfficeIcon },
      { name: 'Material Planning', href: '/mrp', icon: CalculatorIcon },
      { name: 'Capacity & Schedule', href: '/capacity', icon: CalendarDaysIcon },
      { name: 'Routings', href: '/routings', icon: QueueListIcon },
      { name: 'Shop Floor', href: '/shop-floor', icon: PlayCircleIcon },
      { name: 'HR & Payroll', href: '/hr', icon: IdentificationIcon },
      { name: 'Job Cards', href: '/job-cards', icon: ClipboardDocumentListIcon },
      { name: 'OEE & Downtime', href: '/oee', icon: PresentationChartLineIcon },
      { name: 'Maintenance', href: '/maintenance', icon: WrenchIcon },
      { name: 'Tooling', href: '/tooling', icon: Cog6ToothIcon },
      { name: 'Purchase', href: '/purchase', icon: ShoppingCartIcon },
      { name: 'Purchase Indents', href: '/indents', icon: ClipboardDocumentCheckIcon },
      { name: 'RFQs & Quotes', href: '/rfqs', icon: ScaleIcon },
      { name: 'Goods Receipts', href: '/goods-receipts', icon: InboxArrowDownIcon },
      { name: 'Incoming Inspection', href: '/inspections', icon: ShieldCheckIcon },
      { name: 'NCR / CAPA', href: '/ncrs', icon: ExclamationTriangleIcon },
      { name: 'Instruments & Calibration', href: '/instruments', icon: WrenchScrewdriverIcon },
      { name: 'Part Approval & SPC', href: '/part-approval', icon: ClipboardDocumentCheckIcon },
      { name: 'Scrap', href: '/scrap', icon: ArchiveBoxXMarkIcon },
    ]
  },
  {
    name: 'Sales & CRM',
    icon: CurrencyRupeeIcon,
    children: [
      { name: 'Sales Orders', href: '/sales', icon: CurrencyRupeeIcon },
      { name: 'Dispatch', href: '/dispatches', icon: TruckIcon },
      { name: 'Enquiries', href: '/enquiries', icon: ChatBubbleLeftRightIcon },
      { name: 'Costing', href: '/cost-sheets', icon: CalculatorIcon },
      { name: 'Quotes', href: '/quotes', icon: DocumentTextIcon },
      { name: 'Sales Dashboard', href: '/sales-dashboard', icon: PresentationChartBarIcon },
      { name: 'Rate Contracts', href: '/rate-contracts', icon: DocumentCheckIcon },
      { name: 'Customer Schedules', href: '/customer-schedules', icon: CalendarDaysIcon },
      { name: 'Price Lists', href: '/price-lists', icon: TagIcon },
      { name: 'Demand Forecasting', href: '/demand-forecasting', icon: ChartBarIcon },
      { name: 'Customers', href: '/customers', icon: UsersIcon },
    ]
  },
  {
    name: 'Finance',
    icon: DocumentTextIcon,
    children: [
      { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon },
      { name: 'Payments Received', href: '/payments', icon: BanknotesIcon },
      { name: 'Vendor Bills & Payments', href: '/payables', icon: BanknotesIcon },
      { name: 'GST Reports', href: '/gst-reports', icon: ChartBarIcon },
      { name: 'HSN Codes', href: '/hsn-codes', icon: HashtagIcon },
    ]
  },
  {
    name: 'Vendors',
    icon: BuildingOfficeIcon,
    children: [
      { name: 'Vendors', href: '/vendors', icon: BuildingOfficeIcon },
      { name: 'Approved Vendors', href: '/approved-vendors', icon: CheckBadgeIcon },
    ]
  },
  {
    name: 'Analytics & Reports',
    icon: ChartPieIcon,
    children: [
      { name: 'Analytics', href: '/analytics', icon: ChartPieIcon },
      { name: 'MIS Dashboard', href: '/mis', icon: PresentationChartLineIcon },
      { name: 'Reports', href: '/reports', icon: ChartBarIcon },
    ]
  },
  {
    name: 'Master Data',
    icon: Squares2X2Icon,
    children: [
      { name: 'Master Data', href: '/master-data', icon: Squares2X2Icon },
      { name: 'GST Rates', href: '/master-data/gst-rates', icon: ReceiptPercentIcon },
      { name: 'Engineering Changes', href: '/master-data/ecns', icon: PencilSquareIcon },
      { name: 'Documents & Drawings', href: '/documents', icon: FolderOpenIcon },
    ]
  },
  {
    name: 'Administration',
    icon: CogIcon,
    children: [
      { name: 'User Management', href: '/users', icon: UserGroupIcon },
      { name: 'Role Management', href: '/roles', icon: ShieldCheckIcon },
      { name: 'Company Settings', href: '/company-settings', icon: BuildingOfficeIcon },
      { name: 'Settings', href: '/settings', icon: CogIcon },
      { name: 'Integrations', href: '/integrations', icon: ArrowsRightLeftIcon },
      { name: 'Audit Trail', href: '/audit-log', icon: ClipboardDocumentListIcon },
      { name: 'Backup', href: '/backup', icon: CircleStackIcon },
    ]
  },
  { name: 'Notifications', href: '/notifications', icon: BellIcon },
  { name: 'My Profile', href: '/profile', icon: UserIcon },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const { canOpen } = usePermissions();

  // Only the pages the role may open; a group with nothing left in it goes too
  const visibleNavigation = navigation
    .map((item) => (item.children ? { ...item, children: item.children.filter((child) => canOpen(child.href!)) } : item))
    .filter((item) => (item.children ? item.children.length > 0 : canOpen(item.href!)));

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const NavigationItems = ({ items, isMobile = false }: { items: NavigationItem[], isMobile?: boolean }) => (
    <>
      {items.map((item) => {
        const isExpanded = expandedCategories.has(item.name);
        
        return (
          <li key={item.name}>
            {item.children ? (
              <>
                <button
                  type="button"
                  className="text-indigo-200 hover:text-white hover:bg-indigo-700 group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                  onClick={() => toggleCategory(item.name)}
                >
                  <item.icon className="h-6 w-6 shrink-0" aria-hidden={true} />
                  <span className="flex-1 text-left">{item.name}</span>
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5" aria-hidden={true} />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" aria-hidden={true} />
                  )}
                </button>
                {isExpanded && (
                  <ul className="ml-6 mt-1 space-y-1">
                    {item.children.map((child) => (
                      <li key={child.name}>
                        <NavLink
                          to={child.href!}
                          className={({ isActive }) =>
                            classNames(
                              isActive
                                ? 'bg-indigo-700 text-white'
                                : 'text-indigo-200 hover:text-white hover:bg-indigo-700',
                              'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                            )
                          }
                          onClick={() => isMobile && setOpen(false)}
                        >
                          <child.icon className="h-5 w-5 shrink-0" aria-hidden={true} />
                          {child.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <NavLink
                to={item.href!}
                className={({ isActive }) =>
                  classNames(
                    isActive
                      ? 'bg-indigo-700 text-white'
                      : 'text-indigo-200 hover:text-white hover:bg-indigo-700',
                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                  )
                }
                onClick={() => isMobile && setOpen(false)}
              >
                <item.icon className="h-6 w-6 shrink-0" aria-hidden={true} />
                {item.name}
              </NavLink>
            )}
          </li>
        );
      })}
    </>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden={true} />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-600 px-6 pb-2">
                  <div className="flex h-16 shrink-0 items-center">
                    <h1 className="text-white text-xl font-bold">RightFitERP</h1>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          <NavigationItems items={visibleNavigation} isMobile={true} />
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-600 px-6">
          <div className="flex h-16 shrink-0 items-center">
            <h1 className="text-white text-xl font-bold">RightFitERP</h1>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  <NavigationItems items={visibleNavigation} />
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;